/*
 * Copyright 2025 Alexander Orlov <alexander.orlov@loxal.net>. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

package net.loxal.finder.client

import kotlinx.browser.document
import kotlinx.browser.localStorage
import kotlinx.browser.window
import kotlinx.dom.clear
import org.w3c.dom.*
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent
import org.w3c.xhr.XMLHttpRequest

// Prompt Finder as new name/theme?
private fun main() {
    document.addEventListener("loxal.sitekit-container-template-fetched", {
        console.asDynamic().debug("loxal.sitekit-container-template-fetched")
        Finder(currentScript as HTMLScriptElement)
    })

    if (document.location?.pathname != "/app/gadget/main.html") {
        Finder(currentScript as HTMLScriptElement)
    }
}

internal val currentScript = document.currentScript

class Finder(private val pageFinderInit: HTMLScriptElement) {
    private data class Finding(
        val title: String = "",
        val body: String = "",
        val url: String = "",
        val urlRaw: String = "",
        val labels: Array<String> = arrayOf()
    )

    private data class Findings(val query: String, val results: Array<Finding> = arrayOf())
    private data class Suggestions(val results: Array<String> = arrayOf())

    private var siteId = pageFinderInit.getAttribute("data-siteId")

    private val findingsContainer = document.createElement("dl") as HTMLDListElement
    internal val finderService =
        if (window.location.port === "1180")
            "${window.location.origin}/sites"
        else
            "$FINDER_SERVICE_URL/sites"

    private val finderEndpoint = "search"
    private val autocompleteEndpoint = "autocomplete"
    val pageFinderComponent = document.createElement("page-finder") as HTMLElement
    private val finder: HTMLInputElement = document.createElement("input") as HTMLInputElement
    private val debugView = document.createElement("dl") as HTMLDListElement

    private val pageFinderContainer = document.createElement("div") as HTMLDivElement
    private val baseFontSize =
        window.getComputedStyle(document.documentElement!!).getPropertyValue("font-size").substringBefore("px")
            .toFloat().toInt()
    private val defaultFinderWidthInRem = 145

    init {
        document.addEventListener("keydown", {
            it as KeyboardEvent
            if ((it.metaKey || it.ctrlKey) && it.key === "k") {
                finder.focus()
            }
        })
        document.addEventListener("loxal.finder-searchQuery", {
            if (siteId != "a9ede989-9d94-41d1-8571-a008318b01db") {
                it as CustomEvent
                finder.placeholder = "\uD83D\uDD0D ${it.detail}"
                siteId =
                    localStorage["siteId"] // overloading the single responsibility principle, move to another event?
                console.asDynamic().debug("Finder siteId changed: $siteId")
            }
        })

        finder.addEventListener("blur", {
            findingsContainer.clear()
            finder.style.borderRadius = "unset"
        })
        finder.addEventListener("change", {
            console.asDynamic().debug("finder change: |${finder.value}|")
        })
        finder.addEventListener("input", {
            console.asDynamic().debug("finder input: |${finder.value}|")
            if (finder.value.isBlank()) {
                findingsContainer.style.display = "none"
            }
        })
        document.addEventListener("keydown", {
            it as KeyboardEvent
            if (it.key === "/" && document.activeElement?.localName === "body") {
                finder.focus()
                it.preventDefault()
            }
        })

        buildPageFinder()
        conditionallyInjectPageFinderIntoWebsite()
    }

    private fun selfTest() {
        debugView.clear()
        validateServiceCall("search")
        validateServiceCall("autocomplete")
    }

    private fun validateServiceCall(apiEndpoint: String) {
        val xhr = XMLHttpRequest()
        xhr.open("GET", "$finderService/$siteId/$apiEndpoint?query=site")
        xhr.send()
        xhr.onload = {
            val response = it.target as XMLHttpRequest
            val dt = document.createElement("dt") as HTMLElement
            debugView.appendChild(dt)
            val dd = document.createElement("dd") as HTMLElement
            if (response.status == 200.toShort() && response.responseText.length > 14) {
                dt.style.color = "#191"
                dt.textContent = "PASSED: $apiEndpoint"
            } else {
                dt.style.color = "#911"
                dt.textContent = "FAILED: $apiEndpoint"
            }
            dd.textContent = "Status: ${response.status} | Response Length: ${response.responseText.length}"
            debugView.appendChild(dd)
        }
    }

    private fun conditionallyInjectPageFinderIntoWebsite() {
        val hiddenBehindFlag: String? = pageFinderInit.getAttribute("data-hidden-behind-query-flag")

        if (hiddenBehindFlag.isNullOrBlank()) {
            showPageFinder()
        } else if (window.location.search.contains(hiddenBehindFlag)) {
            showPageFinder()
        }
    }

    private fun showPageFinder() {
        val resultsStyle = pageFinderInit.getAttribute("data-results-style")
        val style = document.createElement("style") as HTMLStyleElement
        style.innerHTML = """
            .pf-highlight {font-weight: bold;} 
            .even {background-color: hsla(130, 0%, 95%, 1);} 
            .odd {background-color: hsla(130, 0%, 85%, 1);} 
            .pf-found-item-container:hover {filter: brightness(50%);}
        """.trimMargin() // even / odd
        finder.appendChild(style)
        findingsContainer.style.cssText =
            if (resultsStyle.isNullOrBlank())
                """
                    min-height: 0rem; max-height: 33rem;
                    margin-top: 0rem; overflow-y: auto; overflow-x: hidden;
                    width: ${
                    (if (finder.getAttribute("width")
                            ?.toInt() == 0
                    ) defaultFinderWidthInRem * baseFontSize else finder.getAttribute("width")?.toInt()!!) - 0
                }px;
                    border-radius: 0 0 .5rem .5rem;
                """.trimMargin()
            else
                resultsStyle
        findingsContainer.style.background = "#fff"
        findingsContainer.style.zIndex = "99"
        findingsContainer.style.position = "absolute"
        findingsContainer.style.fontFamily = "sans-serif"

        pageFinderContainer.id = "sitesearch-page-finder"
        encapsulateAsComponent()

        pageFinderContainer.appendChild(finder)
        pageFinderContainer.appendChild(findingsContainer)
        findingsContainer.style.display = "none"
    }

    private fun encapsulateAsComponent() {
        val parentContainerId: String? = pageFinderInit.getAttribute("data-append-as-child-to")
        val parent =
            if (parentContainerId.isNullOrBlank()) {
                pageFinderInit.parentElement
            } else {
                val parentContainer = document.querySelector(parentContainerId) as HTMLElement?
                console.asDynamic().debug("Inserted into: ${parentContainer?.outerHTML}")
                parentContainer ?: pageFinderInit.parentElement
            } as HTMLElement

        resetInheritedStylePropertiesProtectingCssBleeding(pageFinderComponent)
        pageFinderComponent.appendChild(pageFinderContainer)
        parent.attachShadow(ShadowRootInit(ShadowRootMode.OPEN))
            .appendChild(pageFinderComponent)
        document.dispatchEvent(Event("loxal.finder-load"))
    }

    companion object Finder {
        internal const val FINDER_SERVICE_URL = "https://search.loxal.net"
        private const val BODY_SNIPPET_SIZE_LIMIT = 180
    }

    private fun buildPageFinder() {
        val finderStyle = pageFinderInit.getAttribute("data-search-style")
        finder.type = "search"
        finder.spellcheck = true
        finder.title = "Finder"
        finder.placeholder = "\uD83D\uDD0DFind... ${if (window.navigator.userAgent.contains("Mac")) "⌘" else "Ctrl+"}K"
        finder.accessKey = "p"
        finder.style.cssText =
            if (finderStyle.isNullOrBlank())
                "width: ${defaultFinderWidthInRem}rem; font-size: 2rem; text-indent: .5rem; border-radius: 1rem 1rem 0 0; border: hidden; padding: .3rem;"
            else
                finderStyle

        finder.width = when {
            finder.style.width.endsWith("rem") -> {
                finder.style.width.substringBeforeLast("rem").toFloat().toInt() * baseFontSize
            }

            finder.style.width.endsWith("em") -> {
                finder.style.width.substringBeforeLast("em").toFloat().toInt() * baseFontSize
            }

            finder.style.width.endsWith("px") -> {
                finder.style.width.substringBeforeLast("px").toFloat().toInt()
            }

            else -> {
                defaultFinderWidthInRem * baseFontSize
            }
        }

        finder.addEventListener("keydown", {
            it as KeyboardEvent
            debugView.remove()
            if (it.key === "Enter") {
                if (finder.value.isBlank()) {
                    findingsContainer.style.display = "none"
                } else {
                    search()
                }
            } else if (it.key === "Backspace" || it.key !== "Escape") {
                autocomplete()
            } else if (it.key === "Escape") {
                findingsContainer.style.display = "none"
            }
        })
    }

    private fun autocomplete() {
        val xhr = XMLHttpRequest()
        xhr.open("GET", "$finderService/$siteId/$autocompleteEndpoint?query=${finder.value}")
        xhr.send()
        xhr.onload = {
            if (xhr.status == 200.toShort()) {
                findingsContainer.clear()
                finder.style.borderRadius = "1rem 1rem 0 0"
                val suggestions = JSON.parse<dynamic>(xhr.responseText)
                suggestions.results.forEach { suggestion, idx ->
                    val suggestionEntry = document.createElement("dd") as HTMLElement
                    suggestionEntry.className = "pf-found-item-container ${if (idx % 2 == 0) "even" else "odd"}"
                    suggestionEntry.style.borderBottom = ".01rem solid #ccc"
                    suggestionEntry.style.marginLeft = "0"
                    suggestionEntry.style.padding = ".5rem"
                    suggestionEntry.style.fontSize = "1.5rem"
                    suggestionEntry.innerHTML = "$suggestion&hellip;"
                    suggestionEntry.onmousedown = { // onclick, onmouseup do not work
                        console.asDynamic().debug(suggestionEntry.innerText)
                        finder.value = suggestionEntry.innerText
                        search()
                    }
                    findingsContainer.appendChild(suggestionEntry)
                }
                if (suggestions.results.length !== 0) {
                    findingsContainer.style.display = "block"
                }
            }
        }
        xhr.onerror = {
            console.asDynamic().debug("Autocomplete error: ${xhr.response}")
        }
    }

    private fun pushAnalytics() {
        val analyticsTrackingId: String? = pageFinderInit.getAttribute("data-analyticsTrackingId")
        if (!analyticsTrackingId.isNullOrBlank()) {
            console.asDynamic().debug("Sending data to analyticsTrackingId: $analyticsTrackingId")
            val xhr = XMLHttpRequest()
            val searchQueryParam = "query"
            val analyticsEndpoint = "https://www.google-analytics.com/r/collect?v=1&cid=0&tid="
            xhr.open("GET", "$analyticsEndpoint$analyticsTrackingId&dl=%3F$searchQueryParam%3D${finder.value}")
            xhr.send()
            xhr.onload = {
                console.asDynamic().debug("Search term pushed to analytics: ${finder.value}")
            }
            xhr.onerror = {
                console.asDynamic().debug("Analytics error: ${xhr.response}")
            }
        }
    }

    private fun search() {
        if (finder.value === "/selfTest") {
            findingsContainer.style.display = "none"
            debugView.style.background = "#eee"
            debugView.style.position = "absolute"
            debugView.style.borderRadius = ".5rem"
            debugView.style.padding = "1rem"
            finder.parentNode?.appendChild(debugView)
            selfTest()
            return
        }

        pushAnalytics()

        val xhr = XMLHttpRequest()
        xhr.open("GET", "$finderService/$siteId/$finderEndpoint?query=${finder.value}")
        xhr.send()
        xhr.onload = {
            findingsContainer.clear()
            if (xhr.status == 200.toShort()) {
                val findings = JSON.parse<dynamic>(xhr.responseText)
                findings.results.forEach { finding, idx ->
                    val foundItemContainer = document.createElement("div") as HTMLDivElement
                    foundItemContainer.className = "pf-found-item-container ${if (idx % 2 == 0) "even" else "odd"}"
                    foundItemContainer.style.padding = ".5rem .5rem .5rem .5rem"
                    findingsContainer.appendChild(foundItemContainer)
                    foundItemContainer.onmousedown = {
                        console.asDynamic().debug("resultItemContainer urlRaw: ${finding.urlRaw}")
                        window.location.assign(finding.urlRaw)
                    }
                    val dtTitle = document.createElement("dt") as HTMLElement
                    dtTitle.innerHTML = finding.title
                    dtTitle.setAttribute(
                        "style",
                        "margin-bottom: .5rem;"
                    )
                    foundItemContainer.appendChild(dtTitle)
                    val ddBody = document.createElement("dd") as HTMLElement
                    ddBody.innerHTML = finding.body.substring(0, BODY_SNIPPET_SIZE_LIMIT)
                    ddBody.setAttribute("style", "margin-bottom: .5rem; text-align: justify;")
                    foundItemContainer.appendChild(ddBody)
                    val ddLabels = document.createElement("dd") as HTMLElement
                    ddLabels.innerHTML = if (finding.labels.length === 0) "" else "\uD83C\uDFF7️&nbsp;"
                    ddLabels.innerHTML += finding.labels.join(", ")
                    ddLabels.setAttribute("style", "margin-bottom: .5rem; float: right;")
                    foundItemContainer.appendChild(ddLabels)
                    val ddUrl = document.createElement("dd") as HTMLElement
                    ddUrl.innerHTML = "<a style=\"text-decoration:none\" href=\"${finding.urlRaw}\">${finding.url}</a>"
                    ddUrl.setAttribute("style", "margin-bottom: 1rem;")
                    foundItemContainer.appendChild(ddUrl)
                }
                if (findings.results.length === 0) {
                    val dtTitle = document.createElement("dt") as HTMLElement
                    dtTitle.innerHTML = "&hellip;"
                    dtTitle.setAttribute(
                        "style", "margin-bottom: .5rem;" +
                                "text-align: center;" +
                                "font-size: 2rem;"
                    )
                    findingsContainer.appendChild(dtTitle)
                }
                findingsContainer.style.display = "block"
            }
        }
        xhr.onerror = {
            console.asDynamic().debug("Error: ${xhr.response}")
        }
    }

    private fun resetInheritedStylePropertiesProtectingCssBleeding(dirtyElement: HTMLElement) {
        window.getComputedStyle(dirtyElement).cssText.split(": ; ")
            .forEach { cssProperty -> dirtyElement.style.setProperty(cssProperty, "initial") }
    }
}